import { useTheme } from '../contexts/ThemeContext';

/**
 * Surface/text tokens for the Predictions UI.
 *
 * This screen's visual language is translucent "glass" overlays (bg-white/5,
 * border-white/10, text-white/40 …), which only read correctly on a dark
 * ground — on the five light themes they render as white-on-white and the
 * page appears blank. These tokens keep the original dark treatment intact
 * and swap in solid neutrals for light themes.
 *
 * Note: white text sitting on a coloured fill (gradient buttons, badges) is
 * already correct in both modes and is intentionally left hardcoded.
 */
export function usePredictionUI() {
  const { theme, isDark } = useTheme();

  return {
    theme,
    isDark,

    // Text
    title:  isDark ? 'text-white'    : 'text-gray-900',
    dim:    isDark ? 'text-white/60' : 'text-gray-600',
    label:  isDark ? 'text-white/40' : 'text-gray-500',
    faint:  isDark ? 'text-white/30' : 'text-gray-400',

    // Hover text (paired with `label`/`faint` above)
    hoverTitle: isDark ? 'hover:text-white/80' : 'hover:text-gray-900',
    hoverDim:   isDark ? 'hover:text-white/60' : 'hover:text-gray-700',

    // Surfaces — panel is the outermost card, surface/surfaceHi nest inside it
    panel:     isDark ? 'bg-white/3'  : 'bg-gray-50',
    surface:   isDark ? 'bg-white/5'  : 'bg-gray-100',
    surfaceHi: isDark ? 'bg-white/10' : 'bg-gray-200',
    hoverSurface: isDark ? 'hover:bg-white/8'  : 'hover:bg-gray-100',
    activeSurface: isDark ? 'bg-white/15' : 'bg-gray-200',

    // Borders
    border:     isDark ? 'border-white/10' : 'border-gray-200',
    borderSoft: isDark ? 'border-white/8'  : 'border-gray-200',
    borderHi:   isDark ? 'border-white/20' : 'border-gray-300',
    hoverBorder: isDark ? 'hover:border-white/25' : 'hover:border-gray-400',

    // Floating dropdown panel — needs an opaque ground in both modes
    popover: isDark ? 'bg-slate-900 border-white/10' : 'bg-white border-gray-200',

    // Placeholder text inside inputs
    placeholder: isDark ? 'placeholder-white/30' : 'placeholder-gray-400',

    // Hairline divider
    divider: isDark ? 'bg-white/8' : 'bg-gray-200',
  };
}
